import { resetReducer, makeDuck, promiseHandler } from 'cooldux';
import { omit } from 'lodash';
import Joi from 'joi-browser';

import validator from './validator';
import { apiFetch, setToken } from '../lib/fetch';
import config from '../config';
import { fbauth, authProviders, createCredential } from '../lib/firebase';
  

function handleFBResult(result) {
  return fbauth.currentUser.getIdToken().then((idToken) => {
    const body = {
        details: JSON.parse(JSON.stringify(result)),
        idToken
    };
    if(result.credential) {
      body.provider = result.credential.providerId;
    } else {
      body.provider = 'email';
    }
    return body;
  })
  .then((body) => {
    console.log('body', body);
    const options = { method: 'POST', body };
    return apiFetch('/oauth', options);
  });
}

const userSchema = {
  password: Joi.string().alphanum().min(8).max(10000).required(),
  confirmPassword: Joi.string().alphanum().min(8).max(10000).required(),
  email: Joi.string().email().required(),
};

const { verifyEnd, verifyHandler, verifyReducer } = promiseHandler('verify', { namespace: 'user', throwErrors: true });

const duck = makeDuck({
  validate: (token) => apiFetch(`/users/validate/${token}`, { method: 'PUT' }),
  signup: (newUser) => {  
    const valid = validator(newUser, userSchema);
    if (valid.error) {
      return Promise.reject(valid.error);
    }
    if (newUser.password !== newUser.confirmPassword) {
      return Promise.reject('passwords don\'t match');
    }
    const options = { method: 'POST', body: omit(newUser, 'confirmPassword') };
    return apiFetch('/users', options);
  },
  passwordReset: (email) => {
    const valid = validator({ email }, { email: userSchema.email });
    if (valid.error) {
      return Promise.reject(valid.error);
    }
    const options = { method: 'POST', body: { email } };
    return apiFetch('/users/reset', options);
  },
  updatePassword: (token, password, confirmPassword) => {
    // eslint-disable-next-line
    const valid = validator({ password, confirmPassword }, { password: userSchema.password, confirmPassword: userSchema.confirmPassword });
    if (valid.error) {
      return Promise.reject(valid.error);
    }
    if (password !== confirmPassword) {
      return Promise.reject('passwords don\'t match');
    }
    const options = { method: 'PUT', body: { password, verify_password: confirmPassword } };
    return apiFetch(`/users/reset/${token}`, options);
  },
  auth: (email, password) => {
    // eslint-disable-next-line
    const valid = validator({ email, password }, { email: Joi.string().required(), password: Joi.string().required() });
    if (valid.error) {
      return Promise.reject(valid.error);
    }
    const options = { method: 'POST', body: { email, password } };
    return apiFetch('/auth', options);
  },
  checkFBRedirect: () => {
    return fbauth.getRedirectResult().then(function(result) {
      console.log('redir result', result);
      if(result.user) {
        if(sessionStorage.getItem('pendingFBCred')) {
          let pendingCred = JSON.parse(sessionStorage.getItem('pendingFBCred'));
          console.log('pre-pendingcred', pendingCred);
          pendingCred = createCredential(pendingCred);
          console.log('pendingcred', pendingCred);
          sessionStorage.removeItem('pendingFBCred');
          return result.user.linkAndRetrieveDataWithCredential(pendingCred).then(function(usercred) {
            console.log(result, usercred);
            return handleFBResult(result);
          });
        }
        return handleFBResult(result);
      }
      //throw new Error('not fb redir');
      return null;
    }).catch(function(error) {
      if (error.code === 'auth/account-exists-with-different-credential') {
        console.log('error', error);
        const pendingCred = error.credential;
        // Get sign-in methods for this email.
        sessionStorage.setItem('pendingFBCred', JSON.stringify(pendingCred));
        return fbauth.fetchSignInMethodsForEmail(error.email).then(function(methods) {
          console.log(methods, error.email);
          // TODO: implement getProviderForProviderId.
          // var provider3 = getProviderForProviderId(methods[0]);
          // At this point, you should let the user know that he already has an account
          // but with a different provider, and let him validate the fact he wants to
          // sign in with this provider.
          // Sign in to provider. Note: browsers usually block popup triggered asynchronously,
          // so in real scenario you should ask the user to click on a "continue" button
          // that will trigger the signInWithPopup.
          return fbauth.signInWithRedirect(authProviders[methods[0]]);
        });
      }
      throw error;
    });
  },
  oauth: (provider) => {
    return fbauth.signInWithRedirect(authProviders[provider]);
  },
  emailSignin: (email) => {
    return fbauth.sendSignInLinkToEmail(email, config.fbActionCodeSettings)
      .then(function() {
        window.localStorage.setItem('emailForSignIn', email);
        return 'ok';
      });
  },
  emailSigninRedir: () => {
    if (fbauth.isSignInWithEmailLink(window.location.href)) {
      let email = window.localStorage.getItem('emailForSignIn');
      if (!email) {
        email = window.prompt('Please provide your email for confirmation');
      }
      return fbauth.signInWithEmailLink(email, window.location.href)
        .then(function(result) {
          window.localStorage.removeItem('emailForSignIn');
          console.log('result', result);
          return handleFBResult(result);
        });
    }
    return Promise.reject('invalid link');
  },
  logout: () => {
    return apiFetch('/logout')
    .catch(() => null)
    .then(() => {
      return fbauth.signOut().then(() => null).catch(() => null);
    })
    .then(() => {
      setToken(null);
      return null;
    });
  },
}, { namespace: 'user', throwErrors: true });

const {
  initialStateCombined,
  reducerCombined,
  updatePasswordEnd,
  emailSigninRedirEnd,
  checkFBRedirectEnd,  
} = duck;

export function verify() {
  return (dispatch, getState) => {
    const { auth } = getState().user;
    const promise = auth ? Promise.resolve(auth) : apiFetch('/users/me');
    return verifyHandler(promise, dispatch);
  }
}

export const { validate, auth, logout, passwordReset, updatePassword, signup, oauth, emailSignin, emailSigninRedir, checkFBRedirect } = duck;

const reducer = resetReducer(initialStateCombined, (state, action) => {
  state = reducerCombined(state, action);
  state = verifyReducer(state, action);
  const { payload } = action;
  switch (action.type) {
    case verifyEnd.type:
      return { ...state, auth: payload };
    case updatePasswordEnd.type:
      if(payload && payload.token) {
        setToken(payload.token);
      }
      return { ...state, auth: payload };
    case emailSigninRedirEnd.type:
      if(payload && payload.token) {
        setToken(payload.token);
      }
      return { ...state, auth: payload };
    case checkFBRedirectEnd.type:
      if(payload && payload.token) {
        setToken(payload.token);
      }
      return { ...state, auth: payload };
    default:
      return state;
  }
});

export default reducer;
